import type { PortalUserType } from './portal.types';
import type { AllowedLinkType } from './portal-users.types';

/**
 * Peso da hierarquia de tipos. Espelha o USER_TYPE_WEIGHT do dashboard-api
 * (`seller-group-context.utils.ts`), que é a fonte da verdade — aqui só
 * escondemos o que não cabe na tela; quem autoriza é o backend.
 * Se a hierarquia mudar lá, atualize aqui.
 */
export const USER_TYPE_WEIGHT: Record<PortalUserType, number> = {
  INTERNAL: 3,
  MASTER: 2,
  ADMIN: 1,
  USER: 0,
};

/**
 * Se o requester pode conceder/gerir alguém do tipo alvo naquela operação.
 *
 * O tipo é sempre o do vínculo com AQUELA operação, nunca o `user.type`
 * global: quem é MASTER numa e ADMIN noutra não pode conceder MASTER na
 * segunda.
 *
 * Sem o tipo (janela de deploy em que o `/portal/me` ainda não devolve
 * `type`) assumimos ADMIN — o comportamento anterior a esta feature. Assumir
 * "nada" tornaria todo vínculo inegerenciável e quebraria a tela.
 */
export const canGrantType = (
  requesterType: PortalUserType | undefined,
  targetType: PortalUserType,
): boolean =>
  USER_TYPE_WEIGHT[requesterType ?? 'ADMIN'] >= USER_TYPE_WEIGHT[targetType];

/** Tipos que o requester pode conceder numa operação, do maior para o menor. */
export const grantableTypes = (
  requesterTypeInSellerGroup?: PortalUserType,
): AllowedLinkType[] =>
  (['MASTER', 'ADMIN', 'USER'] as AllowedLinkType[]).filter((candidate) =>
    canGrantType(requesterTypeInSellerGroup, candidate),
  );

/**
 * Operação que habilita o requester a alterar a senha do alvo, ou null se
 * nenhuma habilita (o botão não deve aparecer).
 *
 * Senha é operação global (uma conta só no Cognito), então o alvo é avaliado
 * pelo tipo GLOBAL dele, mas o requester pelo tipo POR operação — mesmo
 * racional do backend (changeUserPasswordV2). Basta uma operação compartilhada
 * em que o requester tenha peso suficiente.
 *
 * Diferente de canGrantType, aqui operação sem tipo conhecido NÃO cai no
 * fallback ADMIN: tipo ausente no mapa significa "o requester não tem vínculo
 * ali", não "janela de deploy".
 *
 * Nunca para si mesmo: o caminho self-service é o "esqueci minha senha" do
 * login.
 */
export const changePasswordContext = (
  requesterTypeBySellerGroup: Record<string, PortalUserType | undefined>,
  target: {
    id: string;
    type: PortalUserType;
    links: Array<{ id: string }>;
  },
  requesterId?: string,
): string | null => {
  if (!requesterId || requesterId === target.id) return null;
  const match = target.links.find((link) => {
    const requesterType = requesterTypeBySellerGroup[link.id];
    return (
      requesterType !== undefined && canGrantType(requesterType, target.type)
    );
  });
  return match?.id ?? null;
};
